import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import WidgetCard from './WidgetCard'
import { GhostButton, PrimaryButton, Row } from '../../../layout/styledComponents'
import { parseUtcDate } from '../../../utils/dates'
import {
  getSessionProgressState,
  progressMarkerLabel,
} from '../../../utils/sessionProgress'

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`

const Practitioner = styled.div`
  font-size: 14px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

const TitleLine = styled.h2`
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  letter-spacing: -0.01em;
`

const When = styled.div`
  font-size: 16px;
  color: ${(p) => p.theme?.color?.accent || '#6B9080'};
  font-weight: 600;
`

const Agenda = styled.p`
  margin: 12px 0 18px;
  font-size: 14px;
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
  line-height: 1.5;
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  padding: 12px 14px;
  border-radius: 12px;
`

function describeWhen(scheduledAt, durationMinutes) {
  if (!scheduledAt) return ''
  const target = parseUtcDate(scheduledAt).getTime()
  const now = Date.now()
  const diffMin = Math.round((target - now) / 60000)
  if (diffMin > 0) {
    if (diffMin < 60) return `In ${diffMin} min`
    const hours = Math.floor(diffMin / 60)
    const mins = diffMin % 60
    if (diffMin < 24 * 60) return `In ${hours}h${mins ? ` ${mins}m` : ''}`
    return parseUtcDate(scheduledAt).toLocaleString([], {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
    })
  }
  // diffMin <= 0: session has started. If we're past its scheduled end,
  // call it out as "Just ended (n min ago)".
  const dur = (durationMinutes || 0) * 60_000
  const elapsedMs = now - target
  if (dur > 0 && elapsedMs > dur) {
    const overMin = Math.round((elapsedMs - dur) / 60000)
    return overMin <= 1 ? 'Just ended' : `Ended ${overMin} min ago`
  }
  if (diffMin === 0) return 'Starting now'
  const intoMin = -diffMin
  return intoMin <= 1 ? 'In progress' : `${intoMin} min in`
}

export default function NextSessionCard({ data, skipIfJoinable = false }) {
  const navigate = useNavigate()
  const [tick, setTick] = useState(0)

  // Re-render every minute so countdown stays fresh.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000)
    return () => clearInterval(id)
  }, [])

  if (!data) return null
  // LiveNowCard claims joinable sessions and BlockedSessionsCard claims
  // payment-blocked ones when skipIfJoinable is on — suppress here in
  // either case to avoid rendering the same session twice on Today.
  if (skipIfJoinable && (data.canJoinNow || data.paymentBlocked)) return null

  // Visibility gate: keep the card mounted from "imminent" (~90 min before
  // start) through the end of the post-end grace window. Previously the gate
  // was `minutesAway < -30` which dropped the card 30 min after start —
  // wrong for the longer (now /v/* — 3h post-end) join window since we'd
  // hide the card right when the patient most needs the Join CTA.
  const target = parseUtcDate(data.scheduledAt).getTime()
  const now = Date.now()
  const minutesAway = (target - now) / 60000
  const progress = getSessionProgressState(data)
  if (minutesAway > 90) return null
  if (progress === 'PAST' || progress === 'CANCELLED') return null

  // Trust the server's join window — canJoinNow already accounts for the
  // pre-start lead-in + post-end grace + status / mode gates.
  const canJoin = !!data.canJoinNow
  const markerLabel = progressMarkerLabel(progress)

  return (
    <WidgetCard
      tone={progress === 'IN_PROGRESS' ? 'success' : 'success'}
      title={progress === 'RECENTLY_ENDED' ? 'Just-ended session' : 'Next session'}
      badge={markerLabel || describeWhen(data.scheduledAt, data.durationMinutes)}
      pulse={canJoin}
    >
      <Stack>
        <Practitioner>{data.practitionerName}</Practitioner>
        <TitleLine>{data.title || 'Session'}</TitleLine>
        <When>
          {describeWhen(data.scheduledAt, data.durationMinutes)}
          {tick ? '' : ''}
        </When>
      </Stack>
      {data.agendaPrompt && <Agenda>"{data.agendaPrompt}"</Agenda>}
      <Row $gap="10px" $wrap>
        <PrimaryButton
          onClick={() =>
            canJoin
              ? navigate(data.joinUrl || (data.slug ? `/l/${data.slug}` : `/l/${data.eventId}`))
              : navigate(`/patient-home/sessions/${data.eventId}`)
          }
        >
          {canJoin ? 'Join now' : 'View session'}
        </PrimaryButton>
        {data.canManage && (
          <GhostButton onClick={() => navigate(`/patient-home/sessions/${data.eventId}`)}>
            Reschedule
          </GhostButton>
        )}
      </Row>
    </WidgetCard>
  )
}
